import React, { useState, useEffect, useRef } from 'react';
import { getSymbols } from '../../../services/SymbolsService';
import TickerRow from './TickerRow';
import SelectQuote, { filterSymbolNames, getDefaultQuote } from '../../../components/SelectQuote/SelectQuote';
import '../Dashboard.css';
import { useIntl } from 'react-intl';
import logger from '../../../utils/logger';

/**
 * props:
 * - data: live market data (updates constantly via WebSocket)
 * - onSelectSymbol: (symbol) => drive the top chart
 * - activeSymbol: currently charted symbol (row highlight)
 *
 * The displayed ROW ORDER is frozen: it is computed only on load, on quote/sort
 * change, or when the user clicks Refresh — NOT on every WebSocket tick. Prices
 * still update live in place, but rows no longer jump around.
 */
function Ticker(props) {

    const { formatMessage } = useIntl();
    const t = (id) => formatMessage({ id });

    const [symbols, setSymbols] = useState([]);
    const [quote, setQuote] = useState(getDefaultQuote());
    const [sortBy, setSortBy] = useState('volume'); // volume | change | symbol
    const [ordered, setOrdered] = useState([]);     // frozen, displayed order

    // Always-latest market data without making it a render/effect dependency.
    const dataRef = useRef(props.data);
    dataRef.current = props.data;

    function onQuoteChange(event) { setQuote(event.target.value); }
    function onSortChange(event) { setSortBy(event.target.value); }

    useEffect(() => {
        getSymbols(false)
            .then(symbols => setSymbols(filterSymbolNames(symbols.rows, quote)))
            .catch(err => logger.error(err.response ? err.response.data : err.message));
    }, [quote]);

    // Build (freeze) the row order from the CURRENT data snapshot.
    function buildOrder() {
        const d = dataRef.current || {};
        const vol = (s) => parseFloat((d[s] && (d[s].quoteVolume || d[s].volume)) || 0);
        const chg = (s) => parseFloat((d[s] && d[s].percentChange) || 0);
        const list = symbols.filter(s => d[s] && parseFloat(d[s].close) > 0);
        list.sort((a, b) => {
            if (sortBy === 'change') return chg(b) - chg(a);
            if (sortBy === 'symbol') return a.localeCompare(b);
            return vol(b) - vol(a);
        });
        setOrdered(list);
    }

    // Rebuild on symbol-set load (quote change) and on sort change — user-driven.
    useEffect(() => {
        if (symbols.length) buildOrder();
        else setOrdered([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [symbols, sortBy]);

    // First fill only: once symbols are loaded but the order is still empty
    // (data hadn't arrived yet), build it as soon as data shows up. The guard
    // (`!ordered.length`) means it stops reacting to ticks after the first build,
    // so the list does not jump on subsequent updates.
    useEffect(() => {
        if (symbols.length && !ordered.length && Object.keys(props.data || {}).length) buildOrder();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.data, symbols, ordered.length]);

    return (<React.Fragment>
        <div className="col-12 mb-4">
            <div className="card border-0 shadow">
                <div className="card-header">
                    <div className="row align-items-center g-2">
                        <div className="col-12 col-md">
                            <h2 className="fs-5 fw-bold mb-0">{t('dashboard.market24h')}</h2>
                        </div>
                        <div className="col-auto">
                            <select className="form-select form-select-sm" value={sortBy} onChange={onSortChange} title={t('dashboard.ticker.sortBy')}>
                                <option value="volume">{t('dashboard.ticker.sortVolume')}</option>
                                <option value="change">{t('dashboard.ticker.sortChange')}</option>
                                <option value="symbol">{t('dashboard.ticker.sortSymbol')}</option>
                            </select>
                        </div>
                        <div className="col-auto">
                            <SelectQuote onChange={onQuoteChange} value={quote} />
                        </div>
                        <div className="col-auto">
                            <button type="button" className="btn btn-sm btn-outline-primary" onClick={buildOrder} title={t('dashboard.ticker.refresh')}>
                                <svg className="icon icon-xs" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                    <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
                <div className="table-responsive divScroll">
                    <table className="table align-items-center table-flush table-sm table-hover tableFixHead gh-table-stack">
                        <thead className="thead-light">
                            <tr>
                                <th className="border-bottom" scope="col">{t('common.symbol')}</th>
                                <th className="border-bottom col-2 num" scope="col">{t('dashboard.ticker.close')}</th>
                                <th className="border-bottom col-2 num" scope="col">{t('dashboard.ticker.change24h')}</th>
                                <th className="border-bottom col-2 num" scope="col">{t('dashboard.ticker.open')}</th>
                                <th className="border-bottom col-2 num" scope="col">{t('dashboard.ticker.high')}</th>
                                <th className="border-bottom col-2 num" scope="col">{t('dashboard.ticker.low')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                ordered && ordered.length
                                    ? ordered.map(item => (
                                        <TickerRow
                                            key={item}
                                            symbol={item}
                                            data={props.data[item]}
                                            isActive={props.activeSymbol === item}
                                            onSelectSymbol={props.onSelectSymbol} />
                                    ))
                                    : (<tr><td colSpan={6} className="text-muted">{t('common.loading')}</td></tr>)
                            }
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </React.Fragment>);
}

export default Ticker;
